import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { tooltipIconButtonVariants } from '@/components/ui/tooltip-icon-button'
import { Turn } from '@/hooks/api/use-turn-status-query'
import { cn } from '@/lib/utils'
import { ChevronDown } from 'lucide-react'
import React from 'react'
import { type AgentInfo, parseTurnStep } from '../use-turn-parsed'
import { PlanStep } from './plan-step'
import { StepStatusHeader } from './step-status'

type PlanStepsProps = Readonly<{
  steps: ReturnType<typeof parseTurnStep>[]
  status?: Turn['status']
  error?: Turn['error']
  lastAgent: AgentInfo | null
  expanded: boolean
  onToggleExpand: () => void
  expandedStepIds: Set<string>
  onToggleStepExpand: (stepId: string, enabled?: boolean) => void
  setExpandedStepIds: (updater: (prev: Set<string>) => Set<string>) => void
  renderStepUpdatesList: (step: ReturnType<typeof parseTurnStep>) => React.ReactNode
  workflowId: string
  turnId: string
}>

export const PlanSteps: React.FC<PlanStepsProps> = ({
  steps,
  status,
  error,
  lastAgent,
  expanded,
  onToggleExpand,
  expandedStepIds,
  onToggleStepExpand,
  renderStepUpdatesList,
  workflowId,
  turnId,
}) => {
  return (
    <div className='w-full break-words rounded-lg border border-neutral-75 bg-neutral-25'>
      <div
        className={cn(
          'flex w-full items-center justify-between gap-2 p-2.5',
          steps.length > 0 && expanded && 'border-b-2 border-b-neutral-75',
        )}
      >
        {steps.length > 0 ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                unstyled
                onClick={onToggleExpand}
                className='group/planner-header flex items-center gap-2 text-left outline-offset-1'
              >
                <StepStatusHeader status={status} error={error} agent={lastAgent} />
                <span
                  className={cn(
                    tooltipIconButtonVariants({ size: 'xs' }),
                    'transition-transform',
                    expanded && 'rotate-180',
                  )}
                >
                  <ChevronDown />
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent size='sm' side='right' align='center'>
              {expanded ? 'Collapse' : 'Expand'} steps
            </TooltipContent>
          </Tooltip>
        ) : (
          <div className='flex items-center gap-2 text-left'>
            <StepStatusHeader status={status} error={error} agent={lastAgent} />
          </div>
        )}
      </div>
      {steps.length > 0 && (
        <div
          style={{ maxHeight: expanded ? steps.length * 400 : 0 }}
          className='overflow-hidden transition-[max-height,opacity,height] ease-in-out'
        >
          <div className='flex w-full flex-col gap-1 overflow-y-auto p-2'>
            {steps.map((step) => (
              <PlanStep
                key={step.stepId}
                isExpanded={expandedStepIds.has(step.stepId)}
                onToggleExpand={() => onToggleStepExpand(step.stepId)}
                renderStepUpdatesList={renderStepUpdatesList}
                workflowId={workflowId}
                turnId={turnId}
                stepId={step.stepId}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
